import { useCallback, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { ENTITY_CHANGED_EVENT } from '../utils/activeEntity';

export const FEATURE_ACCESS_CHANGED_EVENT = 'risklo:feature-access-changed';

/**
 * Until the server answers: pages that were already live stay visible, new
 * features stay hidden. Mirrors defaultTiers in featureAccessService.js.
 */
const DEFAULT_HIDDEN = new Set(['trade_watcher', 'ai_assistant']);

/** Which feature switch controls a page (Results / Risk Tools). */
export function featureForPage(page, riskMode) {
  switch (page) {
    case 'dashboard': return 'overview';
    case 'client-reporting': return 'my_portfolio';
    case 'results': return 'brand_results';
    case 'portfolio-simulator': return 'portfolio_simulator';
    case 'my-results': return 'my_results';
    case 'trade-watcher': return 'trade_watcher';
    case 'hedging-risk': return 'hedging_risk';
    case 'prop-firm-planner': return 'prop_firm_planner';
    case 'home': return riskMode === 'apexMae' ? 'rule_risk' : 'blowout_risk';
    default: return null;
  }
}

/**
 * Per-tier feature switches set by Full Admins (Users page → Feature access).
 *
 * The viewer counts as their highest tier, the same order as the sidebar badge
 * and the server: admin > support > staff > sales > max > pro > basic. It uses the
 * effective tiers, so an admin previewing "Pro" sees what Pro users see. Full
 * Admin always sees everything. The server enforces the same switch on its own
 * routes, so this only decides what to show.
 */
export function viewerTierFrom({ isAdmin, isSupport, isVectorAdmin, isSales, isMax, isPro }) {
  if (isAdmin) return 'admin';
  if (isSupport) return 'support';
  if (isVectorAdmin) return 'staff';
  if (isSales) return 'sales';
  if (isMax) return 'max';
  if (isPro) return 'pro';
  return 'basic';
}

export default function useFeatureAccess() {
  const auth = useAuth();
  const { user } = auth;
  const [features, setFeatures] = useState(null);
  const [brandLocked, setBrandLocked] = useState([]);
  const [brand, setBrand] = useState(null);

  const load = useCallback(async () => {
    try {
      // Auth + brand headers: the answer includes the active brand's locks.
      const res = await fetch(API_ENDPOINTS.featuresMe, { headers: getBearerAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setFeatures(data.features || {});
        setBrandLocked(Array.isArray(data.brandLocked) ? data.brandLocked : []);
        setBrand(data.brand || null);
      }
    } catch (_err) {
      /* keep the last answer; unknown features stay hidden */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, user?.email]);

  useEffect(() => {
    window.addEventListener(FEATURE_ACCESS_CHANGED_EVENT, load);
    window.addEventListener(ENTITY_CHANGED_EVENT, load);
    return () => {
      window.removeEventListener(FEATURE_ACCESS_CHANGED_EVENT, load);
      window.removeEventListener(ENTITY_CHANGED_EVENT, load);
    };
  }, [load]);

  const tier = viewerTierFrom(auth);

  const canSee = useCallback((featureKey) => {
    if (tier === 'admin') return true;
    if (!features || !features[featureKey]) return !DEFAULT_HIDDEN.has(featureKey) && (Boolean(user) || featureKey !== 'trade_watcher');
    if (!user) {
      // Signed-out visitors: the switch for Basic decides public pages.
      return featureKey !== 'trade_watcher' && Boolean(features[featureKey].tiers?.includes('basic'));
    }
    return Boolean(features[featureKey].tiers?.includes(tier));
  }, [features, tier, user]);

  /**
   * The active brand hasn't unlocked this feature (Brands page → Features).
   * It stays in the menu, grayed out, and opens a preview instead of the page.
   * Full Admin (not previewing another tier) always gets the real page.
   */
  const isLocked = useCallback(
    (featureKey) => tier !== 'admin' && Boolean(user) && brandLocked.includes(featureKey),
    [tier, user, brandLocked],
  );

  /**
   * RiskLo-wide switches (Admin → Brands → RiskLo-wide settings): on or off for
   * everyone, Admin included. Until the server answers, treat them as on.
   */
  const isGlobalOn = useCallback(
    (featureKey) => (features && features[featureKey] ? features[featureKey].tiers?.length > 0 : true),
    [features],
  );

  /**
   * The active brand turned this feature off for the viewer's tier (its own
   * setting on the Users page). Removed outright: no locked button or upgrade
   * prompt for something the brand doesn't offer.
   */
  const isRemovedForBrand = useCallback(
    (featureKey) => tier !== 'admin' && Boolean(features?.[featureKey]?.brandOverride) && !canSee(featureKey),
    [features, tier, canSee],
  );

  return { canSee, isLocked, isGlobalOn, isRemovedForBrand, brand, tier, loaded: features !== null };
}
